import { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';

const SECTIONS = ['Home', 'About', 'Skills', 'Experience', 'Projects', 'Education', 'Contact'];

const skills = {
  frontend: ['Angular (v8+)', 'React (v16+)', 'Next.js (v13+)', 'HTML5', 'CSS3', 'Bootstrap', 'jQuery', 'JavaScript (ES6+)', 'Responsive Design'],
  backend: ['Node.js'],
  database: ['MySQL', 'PostgreSQL', 'MongoDB'],
  design: ['Figma', 'Wireframing', 'Prototyping', 'UI/UX Design'],
};

const experiences = [
  {
    role: 'Software Engineer',
    company: 'Mobilise App Lab Private Limited · Faridabad',
    date: 'Jan 2025 – Present',
    points: [
      'Developed and maintained scalable front-end applications using React (v16+) and Angular (v8+).',
      'Built reusable React components and optimized state management for performance and maintainability.',
      'Translated Figma wireframes into responsive, production-ready interfaces with pixel-perfect accuracy.',
      'Implemented HTML5, CSS3, and ES6+ best practices for cross-browser compatibility.',
      'Collaborated with designers and backend developers across the full software development lifecycle.',
    ],
  },
  {
    role: 'Software Engineer Intern',
    company: 'Mobilise App Lab Private Limited · Faridabad',
    date: 'Aug 2024 – Dec 2024',
    points: [
      'Designed and developed UI components for internal tools using HTML, CSS, and JavaScript.',
      'Built responsive, scalable front-end modules with Angular.',
      'Used Figma extensively for wireframing and prototyping, translating designs into production interfaces.',
    ],
  },
];

const projects = [
  {
    icon: '🎓',
    title: 'Learning Management System (LMS)',
    desc: 'Comprehensive LMS with Admin, User, and Courses modules. Admin manages roles and permissions; users access courses, track progress, and attempt quizzes. Closely matched Figma design for seamless UX.',
    tags: ['React', 'Node.js', 'MySQL', 'Figma'],
  },
  {
    icon: '🏫',
    title: 'DPS School & E-Learning Platform',
    desc: 'School Management System (attendance, fees, academics) + Digital Learning Platform with student/teacher portals. Deployed for DPS Faridabad, Navi Mumbai, and RK Puram.',
    tags: ['Angular', 'React', 'Figma', 'Bootstrap'],
  },
  {
    icon: '🏢',
    title: 'SO Campus / CLR / BIAL — Facility Management',
    desc: 'Designed and developed PPM, Purchase, Dashboards, and Asset Management modules with complete UI/UX designs in Figma for web and mobile across multiple enterprise clients.',
    tags: ['React', 'Figma', 'Bootstrap', 'MySQL'],
  },
  {
    icon: '👥',
    title: 'Access Global — Employee & DMS',
    desc: 'Employee Management and Document Management System with secure access controls. Built mobile application for HR self-service and designed complete UI/UX prototype in Figma.',
    tags: ['Angular', 'React', 'Figma', 'Node.js'],
  },
  {
    icon: '📱',
    title: 'Access HR India — HR Mobile & Web',
    desc: 'HR mobile application for employee self-service including leave, attendance, and profile management. Designed complete UI/UX in Figma for seamless mobile experience.',
    tags: ['React', 'Figma', 'Bootstrap'],
  },
  {
    icon: '🤖',
    title: 'Face Recognition Attendance System',
    desc: 'Real-time facial recognition attendance system using Python and webcam. Automated Punch-In/Punch-Out tracking with records maintained in a relational database.',
    tags: ['Python', 'OpenCV', 'MySQL'],
  },
];

const education = [
  {
    icon: '🎓',
    degree: 'B.Tech — Computer Science',
    school: 'United Institute of Technology',
    year: '2020 – 2024',
  },
  {
    icon: '📚',
    degree: 'Senior Secondary (12th) — Science',
    school: 'S.V.M. Inter College',
    year: '2019',
  },
  {
    icon: '📖',
    degree: 'Secondary (10th)',
    school: 'S.V.M. Inter College',
    year: '2017',
  },
];

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('Home');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);

      const sectionIds = ['home', 'about', 'skills', 'experience', 'projects', 'education', 'contact'];
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionIds[i]);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActiveSection(SECTIONS[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 70, behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  return (
    <div className="App">
      {/* NAVBAR */}
      <nav className="navbar-custom" style={{ boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.1)' : '0 1px 10px rgba(0,0,0,0.05)' }}>
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <span className="navbar-brand-name">Devesh<span style={{ color: '#0f172a' }}>.</span></span>
            <div className="d-none d-md-flex gap-1">
              {SECTIONS.map((s) => (
                <button
                  key={s}
                  className="nav-link-custom"
                  onClick={() => scrollTo(s.toLowerCase())}
                  style={{
                    background: activeSection === s ? '#eff6ff' : 'transparent',
                    color: activeSection === s ? '#2563eb' : undefined,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
            <button
              className="d-md-none"
              style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#475569' }}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
          {menuOpen && (
            <div className="d-md-none mt-2 pb-2">
              {SECTIONS.map((s) => (
                <button
                  key={s}
                  className="nav-link-custom d-block w-100 text-start"
                  onClick={() => scrollTo(s.toLowerCase())}
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </Container>
      </nav>

      {/* HERO */}
      <section id="home" className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={7} className="mb-5 mb-lg-0">
              <div className="hero-badge">👋 Available for Opportunities</div>
              <h1 className="hero-name">
                Hi, I'm <span>Devesh</span><br />Kumar Dubey
              </h1>
              <p className="hero-role">Full Stack Developer · UI/UX Enthusiast</p>
              <p className="hero-desc">
                2 years of hands-on experience building scalable web applications with React, Angular, and Node.js.
                Passionate about turning design concepts into seamless, production-ready interfaces.
              </p>
              <div className="hero-btns">
                <button className="btn-primary-custom" onClick={() => scrollTo('contact')}>
                  📩 Get In Touch
                </button>
                <button className="btn-outline-custom" onClick={() => scrollTo('projects')}>
                  🚀 View Work
                </button>
                <a
                  href="/deveshDubey_software_developer.pdf"
                  download
                  className="btn-outline-custom"
                  style={{ borderColor: '#16a34a', color: '#16a34a' }}
                >
                  📄 Download CV
                </a>
              </div>
              <div className="hero-stats">
                <div className="stat-item">
                  <div className="stat-number">2+</div>
                  <div className="stat-label">Years Exp.</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">8+</div>
                  <div className="stat-label">Projects</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">10+</div>
                  <div className="stat-label">Technologies</div>
                </div>
              </div>
            </Col>
            <Col lg={5}>
              <div className="hero-image-wrapper">
                <img src="/devesh_profile.png" className="hero-avatar" alt="Devesh Kumar Dubey" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ABOUT */}
      <section id="about" className="section section-alt">
        <Container>
          <div className="section-tag">About Me</div>
          <h2 className="section-title">Who I Am</h2>
          <div className="section-divider"></div>
          <Row className="g-4">
            <Col lg={6}>
              <div className="about-card">
                <h5 style={{ fontSize: '1.05rem', marginBottom: 14 }}>My Story</h5>
                <p>
                  I'm a passionate Full Stack Developer with 2 years of experience building modern web applications.
                  My journey started with a curiosity for how things work on the web, which led me to master both the
                  frontend and the design side of development.
                </p>
                <p style={{ marginTop: 14 }}>
                  I love the challenge of bridging the gap between design and development — taking a Figma prototype
                  and bringing it to life with clean, performant code. I thrive in collaborative teams and enjoy
                  contributing across the full software development lifecycle.
                </p>
                <p style={{ marginTop: 14 }}>
                  When I'm not coding, I'm exploring new frontend frameworks, UI trends, or finding better ways to
                  improve user experiences.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className="about-card">
                <h5 style={{ fontSize: '1.05rem', marginBottom: 14 }}>Quick Info</h5>
                <div className="info-row">
                  <div className="info-icon">📧</div>
                  <span className="info-label">Email</span>
                  <span>rishidubey5676@gmail.com</span>
                </div>
                <div className="info-row">
                  <div className="info-icon">📞</div>
                  <span className="info-label">Phone</span>
                  <span>+91 8840981098</span>
                </div>
                <div className="info-row">
                  <div className="info-icon">📍</div>
                  <span className="info-label">Location</span>
                  <span>Faridabad, India</span>
                </div>
                <div className="info-row">
                  <div className="info-icon">🎓</div>
                  <span className="info-label">Degree</span>
                  <span>B.Tech — Computer Science</span>
                </div>
                <div className="info-row">
                  <div className="info-icon">💼</div>
                  <span className="info-label">Status</span>
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>Open to Work</span>
                </div>
                <div className="info-row">
                  <div className="info-icon">⚡</div>
                  <span className="info-label">Focus</span>
                  <span>React · Angular · UI/UX</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* SKILLS */}
      <section id="skills" className="section">
        <Container>
          <div className="section-tag">Skills</div>
          <h2 className="section-title">My Tech Stack</h2>
          <div className="section-divider"></div>
          <Row className="g-4">
            <Col md={6} lg={3}>
              <div className="skill-category-card">
                <div className="skill-category-icon">⚛️</div>
                <div className="skill-category-title">Frontend</div>
                {skills.frontend.map((s) => (
                  <span key={s} className="skill-tag">{s}</span>
                ))}
              </div>
            </Col>
            <Col md={6} lg={3}>
              <div className="skill-category-card">
                <div className="skill-category-icon">🖥️</div>
                <div className="skill-category-title">Backend</div>
                {skills.backend.map((s) => (
                  <span key={s} className="skill-tag">{s}</span>
                ))}
              </div>
            </Col>
            <Col md={6} lg={3}>
              <div className="skill-category-card">
                <div className="skill-category-icon">🗄️</div>
                <div className="skill-category-title">Databases</div>
                {skills.database.map((s) => (
                  <span key={s} className="skill-tag">{s}</span>
                ))}
              </div>
            </Col>
            <Col md={6} lg={3}>
              <div className="skill-category-card">
                <div className="skill-category-icon">🎨</div>
                <div className="skill-category-title">Design & Tools</div>
                {skills.design.map((s) => (
                  <span key={s} className="skill-tag">{s}</span>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* EXPERIENCE */}
      <section id="experience" className="section section-alt">
        <Container>
          <div className="section-tag">Experience</div>
          <h2 className="section-title">Work History</h2>
          <div className="section-divider"></div>
          <Row>
            <Col lg={8} className="mx-auto">
              <div className="timeline">
                {experiences.map((exp, i) => (
                  <div key={i} className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="exp-card">
                      <span className="exp-date">{exp.date}</span>
                      <div className="exp-role">{exp.role}</div>
                      <div className="exp-company">🏢 {exp.company}</div>
                      <ul className="exp-list">
                        {exp.points.map((p, j) => (
                          <li key={j}>{p}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="section">
        <Container>
          <div className="section-tag">Projects</div>
          <h2 className="section-title">What I've Built</h2>
          <div className="section-divider"></div>
          <Row className="g-4">
            {projects.map((p, i) => (
              <Col key={i} md={6} lg={4}>
                <div className="project-card">
                  <div className="project-header">
                    <div className="project-icon">{p.icon}</div>
                    <h5 className="project-title">{p.title}</h5>
                  </div>
                  <div className="project-body">
                    <p className="project-desc">{p.desc}</p>
                    <div className="project-tags">
                      {p.tags.map((t) => (
                        <span key={t} className="project-tag">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* EDUCATION */}
      <section id="education" className="section section-alt">
        <Container>
          <div className="section-tag">Education</div>
          <h2 className="section-title">Academic Background</h2>
          <div className="section-divider"></div>
          <Row className="g-3">
            {education.map((edu, i) => (
              <Col key={i} md={12} lg={10} className="mx-auto">
                <div className="edu-card">
                  <div className="edu-icon">{edu.icon}</div>
                  <div>
                    <div className="edu-degree">{edu.degree}</div>
                    <div className="edu-school">{edu.school}</div>
                    <span className="edu-year">{edu.year}</span>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CONTACT */}
      <section id="contact" className="section contact-section">
        <Container>
          <div className="section-tag" style={{ background: 'rgba(255,255,255,0.1)', color: '#93c5fd', borderColor: 'rgba(147,197,253,0.3)' }}>
            Contact
          </div>
          <h2 className="section-title" style={{ color: '#fff' }}>Let's Connect</h2>
          <p className="section-subtitle" style={{ color: '#94a3b8', marginBottom: 40 }}>
            Have a project in mind or just want to say hello? Feel free to reach out.
          </p>
          <Row className="g-3">
            <Col md={6} lg={4}>
              <a href="mailto:rishidubey5676@gmail.com" className="contact-card">
                <div className="contact-icon">📧</div>
                <div>
                  <div className="contact-label">Email</div>
                  <div className="contact-value">rishidubey5676@gmail.com</div>
                </div>
              </a>
            </Col>
            <Col md={6} lg={4}>
              <a href="tel:+918840981098" className="contact-card">
                <div className="contact-icon">📞</div>
                <div>
                  <div className="contact-label">Phone</div>
                  <div className="contact-value">+91 8840981098</div>
                </div>
              </a>
            </Col>
            <Col md={6} lg={4}>
              <a href="https://www.linkedin.com/in/devesh0/" target="_blank" rel="noreferrer" className="contact-card">
                <div className="contact-icon">💼</div>
                <div>
                  <div className="contact-label">LinkedIn</div>
                  <div className="contact-value">Devesh Kumar Dubey</div>
                </div>
              </a>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <span>© {new Date().getFullYear()} Devesh Kumar Dubey.</span>
      </footer>
    </div>
  );
}

export default App;
